// Sprint A: Student Profile Images + Inbox/Shares + Profile Edit
import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { User, Camera, IdCard, Image as ImageIcon, Trash2, Upload, Inbox, Send, Share2, Plus, Youtube, FileText, Link as LinkIcon, CheckCircle2 } from "lucide-react";

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF0066]/40";

// ============ Profile Image Uploader ============
export function ProfileImageBlock({ kind, label, icon: Icon, currentUrl, verified, aspect = "1/1", onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/me/profile-images/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      onChange(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    try { await api.delete(`/me/profile-images/${kind}`); toast.success("Removed"); onChange(null); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`profile-${kind}-block`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#FF0066] font-bold flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</div>
        {currentUrl && (verified ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><CheckCircle2 className="w-3 h-3" /> Verified</span>
        ) : (
          <span className="text-[10px] text-amber-300">Pending verify</span>
        ))}
      </div>
      {currentUrl ? (
        <div className={`relative rounded-lg overflow-hidden bg-black`} style={{ aspectRatio: aspect }}>
          <img src={currentUrl} alt={label} className="w-full h-full object-cover" />
          <button onClick={del} className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-red-500/80 hover:bg-red-500 text-white" title="Remove" data-testid={`del-${kind}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full border-2 border-dashed border-white/15 hover:border-[#FF0066]/40 rounded-lg flex flex-col items-center justify-center gap-1.5 text-white/40 hover:text-white/70 transition disabled:opacity-50"
          style={{ aspectRatio: aspect }}
          disabled={busy}
          data-testid={`upload-${kind}-btn`}
        >
          <Upload className="w-5 h-5" />
          <div className="text-xs">{busy ? "Uploading…" : `Upload ${label}`}</div>
          <div className="text-[10px] text-white/40">JPG / PNG / WEBP · max 5MB</div>
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
    </div>
  );
}

export function MyProfileTab() {
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState({ name: "", phone: "", bio: "", city: "", occupation: "" });

  const load = async () => {
    try { const { data } = await api.get("/me/profile"); setProfile(data); setEdit({
      name: data.name || "", phone: data.phone || "", bio: data.bio || "", city: data.city || "", occupation: data.occupation || "",
    }); } catch (e) { /* */ }
  };
  useEffect(() => { load(); }, []);

  if (!profile) return <div className="text-white/40 text-sm py-8">Loading profile…</div>;

  const save = async () => {
    try { await api.patch("/me/profile", edit); toast.success("Profile saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="my-profile-tab">
      <div>
        <div className="text-sm font-semibold text-white mb-3">📸 My Photos</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <ProfileImageBlock kind="profile" label="Profile" icon={User} aspect="1/1"
            currentUrl={profile.profile_image} verified={profile.profile_image_verified}
            onChange={() => load()} />
          <ProfileImageBlock kind="cover" label="Cover" icon={ImageIcon} aspect="16/9"
            currentUrl={profile.cover_image} verified={profile.cover_image_verified}
            onChange={() => load()} />
          <ProfileImageBlock kind="id_card" label="ID Card" icon={IdCard} aspect="16/10"
            currentUrl={profile.id_card_image} verified={profile.id_card_image_verified}
            onChange={() => load()} />
          <ProfileImageBlock kind="classroom" label="Classroom" icon={Camera} aspect="4/3"
            currentUrl={profile.classroom_image} verified={profile.classroom_image_verified}
            onChange={() => load()} />
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="profile-edit-form">
        <div className="text-sm font-semibold text-white">👤 Personal Info</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Full name</div>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inp} />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Phone</div>
            <input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className={inp} />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">City</div>
            <input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} className={inp} />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Occupation</div>
            <input value={edit.occupation} onChange={(e) => setEdit({ ...edit, occupation: e.target.value })} className={inp} />
          </label>
        </div>
        <label className="block">
          <div className="text-xs text-white/50 mb-1">Bio / about you</div>
          <textarea rows={3} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} className={inp} placeholder="Tell about yourself…" />
        </label>
        <button onClick={save} className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-5 py-2 text-sm" data-testid="save-profile">Save changes</button>
      </div>

      <WaOptOutToggle initial={!!profile?.wa_opt_out} onChange={load} />
    </div>
  );
}

function WaOptOutToggle({ initial, onChange }) {
  const [optOut, setOptOut] = useState(initial);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !optOut;
    setSaving(true);
    try {
      await api.post("/me/wa-opt-out", { opt_out: next });
      setOptOut(next);
      toast.success(next
        ? "WhatsApp notifications paused. Aap kabhi bhi resume kar sakte ho."
        : "WhatsApp notifications resumed.");
      onChange && onChange();
    } catch (e) {
      toast.error("Could not update preference");
    } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-3" data-testid="wa-opt-out-card">
      <div>
        <div className="text-sm font-semibold text-white">📱 WhatsApp notifications</div>
        <div className="text-xs text-white/50 mt-0.5">
          {optOut
            ? "Currently OFF — aapko koi class reminder, balance due ya certificate update WA pe nahi milega."
            : "Currently ON — class reminders, balance-due alerts, certificate-ready notifications WhatsApp pe milenge."}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        className={`px-4 py-2 rounded-full text-xs font-semibold transition disabled:opacity-50 ${optOut ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" : "bg-red-500/20 text-red-300 hover:bg-red-500/30"}`}
        data-testid="wa-opt-out-toggle"
      >
        {saving ? "Saving…" : (optOut ? "Resume" : "Pause")}
      </button>
    </div>
  );
}

// ============ Shares (Inbox / Sent / Create) ============
function ResourceTypeIcon({ type }) {
  if (type === "youtube" || type === "vimeo" || type === "video") return <Youtube className="w-4 h-4 text-rose-300" />;
  if (type === "pdf" || type === "doc") return <FileText className="w-4 h-4 text-amber-300" />;
  return <LinkIcon className="w-4 h-4 text-fuchsia-300" />;
}

export function SharesTab() {
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/my-shares/inbox"),
        api.get("/my-shares/sent"),
        api.get("/sharing/settings"),
      ]);
      setInbox(a.data); setSent(b.data); setSettings(c.data);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this share?")) return;
    try { await api.delete(`/shares/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const renderRow = (s, isOwn = false) => (
    <div key={s.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-start gap-3" data-testid={`share-row-${s.id}`}>
      <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center shrink-0"><ResourceTypeIcon type={s.type} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white truncate">{s.title}</div>
        {s.description && <div className="text-[11px] text-white/60 line-clamp-2 mt-0.5">{s.description}</div>}
        <div className="text-[10px] text-white/40 mt-1 flex items-center gap-2 flex-wrap">
          <span>by {s.owner_name} ({s.owner_role})</span>
          <span>•</span>
          <span>{new Date(s.created_at).toLocaleDateString()}</span>
          <span>•</span>
          <span className="uppercase">→ {s.audience_kind}</span>
        </div>
        <a href={s.url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#FF0066] hover:underline">Open ↗</a>
      </div>
      {isOwn && <button onClick={() => del(s.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="shares-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-full border border-white/10 p-1">
          <button onClick={() => setTab("inbox")} className={`px-3 py-1 rounded-full text-xs ${tab === "inbox" ? "bg-[#FF0066] text-white" : "text-white/60"}`} data-testid="shares-inbox-btn"><Inbox className="w-3 h-3 inline mr-1" /> Inbox ({inbox.length})</button>
          <button onClick={() => setTab("sent")} className={`px-3 py-1 rounded-full text-xs ${tab === "sent" ? "bg-[#FF0066] text-white" : "text-white/60"}`} data-testid="shares-sent-btn"><Send className="w-3 h-3 inline mr-1" /> Sent ({sent.length})</button>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold text-xs rounded-full px-4 py-2 inline-flex items-center gap-1" data-testid="open-share-create"><Plus className="w-3.5 h-3.5" /> Share resource</button>
      </div>

      {settings?.student_to_student === false && (
        <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-400/20 rounded-lg px-3 py-2">
          ⚠️ Student-to-student sharing is currently <b>disabled</b> by admin. You can share publicly or in your batch only (if allowed).
        </div>
      )}

      <div className="space-y-2">
        {(tab === "inbox" ? inbox : sent).length === 0 && <div className="text-sm text-white/40 text-center py-8">{tab === "inbox" ? "No shares received yet." : "You haven't shared anything yet."}</div>}
        {(tab === "inbox" ? inbox : sent).map((s) => renderRow(s, tab === "sent"))}
      </div>

      <ShareCreateDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} settings={settings} />
    </div>
  );
}

function ShareCreateDialog({ open, onClose, onSaved, settings }) {
  const empty = { type: "youtube", url: "", title: "", description: "", audience_kind: "public", audience_id: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(empty); /* eslint-disable-next-line */ }, [open]);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/shares", { ...f, audience_id: f.audience_id || null }); toast.success("Shared!"); onSaved(); onClose(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="font-display">Share a resource</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Type</div>
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp}>
              <option value="youtube">YouTube link</option>
              <option value="pdf">PDF / Notes</option>
              <option value="link">Web link</option>
              <option value="doc">Document</option>
              <option value="image">Image</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">URL</div>
            <input required value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className={inp} placeholder="https://…" data-testid="share-url" />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Title</div>
            <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="Cool tutorial / notes pdf" data-testid="share-title" />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Description (optional)</div>
            <textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} />
          </label>
          <label className="block">
            <div className="text-xs text-white/50 mb-1">Audience</div>
            <select value={f.audience_kind} onChange={(e) => setF({ ...f, audience_kind: e.target.value })} className={inp} data-testid="share-audience">
              <option value="public">🌐 Public (anyone)</option>
              <option value="user">👤 Specific user (paste id)</option>
              <option value="batch">👥 My batch</option>
              <option value="course">📚 My course</option>
            </select>
          </label>
          {f.audience_kind !== "public" && (
            <label className="block">
              <div className="text-xs text-white/50 mb-1">{f.audience_kind} id</div>
              <input value={f.audience_id} onChange={(e) => setF({ ...f, audience_id: e.target.value })} className={inp} placeholder="paste id" data-testid="share-audience-id" />
            </label>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-white/10 rounded-full py-2 text-sm">Cancel</button>
            <button disabled={busy} className="flex-1 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full py-2 text-sm disabled:opacity-60" data-testid="submit-share">{busy ? "Sharing…" : "Share"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default { MyProfileTab, SharesTab };
